import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSnackbar } from 'notistack'; 
import { getActivityList, saveTechActivity, deleteTechActivity } from '../../lib/api/techActivity'; 
import { getHolidayList } from '../../lib/api/holidayAction'; 
import { Box } from '@mui/material';
import TechActivityLog from '../../components/techActivity/TechActivityLog';

// 🚀 부모로부터 토글 함수와 상태값을 받아와 연동합니다.
const TechActivityContainer = ({ toggleCalFull, isCalFull }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isSaving, setIsSaving] = useState(false);
    
    // 💡 데이터 성격에 따른 개별 상태 분리
    const [events, setEvents] = useState([]);      // 영업 활동 이벤트 목록
    const [holidays, setHolidays] = useState([]);  // 📅 휴일 목록 (독립 관리)

    // 🚀 실제 작동할 currentDate 상태와 상태 변경 함수를 오늘 날짜 기준으로 선언
    const [currentDate, setCurrentDate] = useState(new Date());
    
    // 💡 불필요한 연도 중복 요청을 방지하기 위한 캐싱 변수 (문자열로 비교)
    const lastFetchedYear = useRef(null);

    // 🚀 [Helper 함수] Date 객체를 "YYYY-MM-DD" 포맷 문자열로 변환하는 함수
    const formatDateToYMD = (dateObj) => {
        if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
            return '';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // =================================================================
    // 🚀 [onDateChange 함수 구현] 하위 로그/달력 컴포넌트에서 호출할 날짜 변경 함수
    // =================================================================
    const onDateChange = useCallback((newDate) => {
        if (!newDate || !(newDate instanceof Date) || isNaN(newDate.getTime())) {
            return;
        }
        console.log("📅 달력/타임라인 날짜 변경 감지:", newDate);
        setCurrentDate(newDate); // 최상위 날짜 상태를 업데이트 ➡️ useEffect를 트리거함
    }, []);

    // 🔍 1. 백엔드에서 휴일 데이터를 별도로 조회해 오는 함수
    const fetchHolidayData = useCallback(async (yearStr) => {
        // 💡 주입받은 값이 없거나 이미 동일한 연도를 조회했다면 중복 호출 방지
        if (!yearStr || lastFetchedYear.current === yearStr) return; 
        
        try {
            // 🚀 API 규격에 맞춰 문자열 연도 파라미터 전송
            const response = await getHolidayList(yearStr);
            if (response.data && response.data.data) {
                
                setHolidays(response.data.data);
                lastFetchedYear.current = yearStr; // 캐싱 연도 기록 유지
            } else {
                setHolidays([]);
            }
        } catch (err) {
            console.error('휴일 목록 로드 오류:', err);
        }
    }, []);

    // 🔍 2. 백엔드에서 영업활동 데이터를 직접 조회해 오는 함수
    const fetchInitialData = useCallback(async (targetDateObj) => {
        try {
            const localUser = localStorage.getItem('user');
            let usrUuid = '';

            if (localUser) {
                const parsedUser = JSON.parse(localUser);
                usrUuid = parsedUser.usrUuid || parsedUser.userUuid || '';
            }

            const currentPath = window.location.pathname;
            let viewType = 'TODAY'; 

            if (currentPath === '/techActivity') {
                viewType = 'ALL';
            } else {
                usrUuid = '';
            }

            // 🚀 인자로 전달받은 날짜 객체를 기반으로 포맷팅을 수행합니다.
            const targetDateObjValid = targetDateObj || new Date();
            const targetDate = formatDateToYMD(targetDateObjValid);
            
            // 💡 [문자열 변환 반영] 연도를 추출한 후 명시적으로 문자열("YYYY")로 변환하여 주입합니다.
            const currentYearStr = String(targetDateObjValid.getFullYear());
            fetchHolidayData(currentYearStr);
            
            const response = await getActivityList(usrUuid, targetDate, viewType);
            
            if (response.data && response.data.list && response.data.list.length > 0) {
                const mappedEvents = response.data.list.map(item => ({
                    id: item.activityId, 
                    title: item.title,
                    start: item.startDatetime, 
                    end: item.endDatetime,
                    backgroundColor: '#1976d2', 
                    borderColor: '#1976d2',
                    allDay: false, 
                    extendedProps: {
                        companyUuid: item.companyUuid,
                        content: item.activityContent,
                        usrUuid: item.usrUuid,
                        usrNm: item.usrNm,
                        meetPlace: item.meetPlace,
                        companyNm: item.companyNm,
                    },
                    googleEventId: item.googleEventId,
                }));
                setEvents(mappedEvents);
            } else {
                setEvents([]);
            }
        } catch (err) {
            console.error('영업활동 목록 로드 오류:', err);
            const message = err.response?.data?.message || err.message || '목록을 불러오는 중 오류가 발생했습니다.';
            enqueueSnackbar(message, { variant: 'error' });
        }
    }, [enqueueSnackbar, fetchHolidayData]);

    // 🔒 [실시간 동기화 라인 변경] 
    useEffect(() => {
        fetchInitialData(currentDate);
    }, [fetchInitialData, currentDate]);

    const onSave = async (formData) => {
        setIsSaving(true);
        try {
            const startDatetime = `${formData.startDate} ${formData.startTime}:00`;
            const endDatetime = `${formData.endDate} ${formData.endTime}:00`;

            const finalData = {
                activityId: formData.activityId || null,
                title: formData.title,
                companyUuid: formData.companyUuid,
                meetPlace : formData.meetPlace,
                activityContent: formData.content,                
                startDatetime: startDatetime,
                endDatetime: endDatetime,
                usrUuid: formData.usrUuid || '',
                googleEventId: formData.googleEventId || null,
            };

            const response = await saveTechActivity(finalData);
            if (response.data.result === 'success') {
                enqueueSnackbar('성공적으로 저장되었습니다.', { variant: 'success' });
                fetchInitialData();
            }
        } catch (err) {
            console.error('저장 오류:', err);
            const message = err.response?.data?.message || err.message || '저장 중 오류가 발생했습니다.';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const onDelete = async (activityId, googleEventId) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        setIsSaving(true);
        try {
            const response = await deleteTechActivity({ activityId, googleEventId });
            if (response.data.result === 'success') {
                enqueueSnackbar('성공적으로 삭제되었습니다.', { variant: 'success' });
                fetchInitialData();
            }
        } catch (err) {
            console.error('삭제 오류:', err);
            const message = err.response?.data?.message || err.message || '삭제 중 오류가 발생했습니다.';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleEventClick = (clickInfo) => {
        console.log('클릭한 이벤트 정보:', clickInfo.event);
    };

    const btnStyle = { 
    display: 'flex',          // 🛠️ 추가: 아이콘 중앙 배치를 위한 플렉스박스 가동
    alignItems: 'center',     // 🛠️ 추가: 세로 중앙 정렬
    justifyContent: 'center',  // 🛠️ 추가: 가로 중앙 정렬
    padding: '8px',           // 🛠️ 교정: 아이콘 크기에 맞춰 사방 여백 통일
    cursor: 'pointer', 
    backgroundColor: '#fff', 
    border: '1px solid #ccc', 
    borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    transition: 'all 0.2s',
    '&:hover': {              // 🛠️ 추가: 마우스 올렸을 때 살짝 강조 효과
        backgroundColor: '#f5f5f5',
        borderColor: '#999'
    }
};

    return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          {window.location.pathname !== '/techActivity' && (
              <Box 
                  sx={{ 
                      position: 'absolute', 
                      top: '6px', 
                      right: '16px', 
                      zIndex: 100 
                  }}
              >
                  <button 
                      onClick={toggleCalFull} 
                      style={btnStyle}
                      title={isCalFull ? "원래 크기로 축소" : "화면 가득 확장"}
                  >
                      {isCalFull ? (
                          /* ↙↘ 축소 아이콘 이미지 */
                          <svg xmlns="http://w3.org" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7"/>
                          </svg>
                      ) : (
                          /* ↖↗ 확장 아이콘 이미지 */
                          <svg xmlns="http://w3.org" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                          </svg>
                      )}
                  </button>
              </Box>
          )}
            <TechActivityLog
                holidays={holidays}
                events={events} 
                currentDate={currentDate}
                onDateChange={setCurrentDate}
                handelEventClick={handleEventClick}
                onSaveActivity={onSave}
                onDeleteActivity={onDelete}
                
                // 💡 화면이 가로로 넓어졌을 때는 등록/수정이 가능하도록 권한을 열어주는 UI 바인딩 속성은 그대로 유지합니다.
                isLocationValid={isCalFull || window.location.pathname === '/techActivity'} 
                isCalFull={isCalFull} 
            />
        </Box>
    );
};

const btnStyle = { 
    padding: '6px 12px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', 
    backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
};

export default TechActivityContainer;
